import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import { DocumentScannerSharp } from '@mui/icons-material'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import ExportButton from 'components/ExportButton'
import { useTranslation } from 'react-i18next'
import { DataGrid } from '@mui/x-data-grid'
import { save as sendNotification } from 'store/notification/notification'
import { resetForm } from 'store/report/form'
import * as snackbar from 'utils/snackbar'

const isHiddenCol = col => col.startsWith('_')
const visibleCols = cols => (cols ?? []).filter(col => !isHiddenCol(col))
const hasNotifyCol = cols => (cols ?? []).some(col => col === '_notify')
const hasResetCol = cols => (cols ?? []).some(col => col === '_reset')
const notifyCode = row => row.notify ?? row._notify

const needsNotify = row => {
    const response = row.response ?? row.response
    return response === null
}

const CHANNEL_NAMES = {
    email: { one: 'email', many: 'emails', label: 'Email' },
    sms: { one: 'SMS', many: 'SMS', label: 'SMS' },
    default: { one: 'notification', many: 'notifications', label: 'Notification' },
}

const channelLabel = channel => CHANNEL_NAMES[channel]?.label ?? CHANNEL_NAMES.default.label

const resetCode = row => row.reset ?? row._reset
const needsReset = row => resetCode(row) != null

export default function ReportResult({ loading, tableData, cols, selectedValues, onRowReset }) {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const [dialog, setDialog] = useState(null)
    const [notifyAll, setNotifyAll] = useState(false)
    const [sending, setSending] = useState(false)
    const [resetting, setResetting] = useState(false)

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <CircularProgress />
            </Box>
        )
    }

    if (!tableData?.length) {
        return (
            <Box sx={{ textAlign: 'center', mt: 5 }}>
                <IconButton disabled>
                    <DocumentScannerSharp sx={{ fontSize: 40 }} />
                </IconButton>
                <Typography variant="h6">{t('No Report Generated Yet')}</Typography>
                <Typography color="text.secondary">
                    {t('Select filters and click "Generate" to view results.')}
                </Typography>
            </Box>
        )
    }

    const isNotify = dialog?.mode === 'notify'
    const isReset = dialog?.mode === 'reset'
    const isResult = dialog?.mode === 'result'
    const target = dialog?.row ?? null
    const targetName = [target?.first_name, target?.last_name].filter(Boolean).join(' ')

    // Skipped entries identify the student by personId only, so resolve the name off the table
    const nameFor = id => {
        const row = (tableData ?? []).find(item => String(item?._id) === String(id))
        return [row?.first_name, row?.last_name].filter(Boolean).join(' ') || `#${id}`
    }

    const pendingRows = (tableData ?? []).filter(needsNotify)
    const recipients = notifyAll && isNotify ? pendingRows : [isNotify ? target : null].filter(Boolean)

    const handleClick = row => {
        setNotifyAll(false)
        setDialog({ mode: 'notify', row })
    }

    const closeDialog = () => {
        if (sending || resetting) return
        setDialog(null)
        setNotifyAll(false)
    }

    const handleSend = async () => {

        const groups = recipients.reduce((acc, row) => {
            const code = notifyCode(row)
            const _id = row._id
            const formTitle = selectedValues?.form_id?.name
            if (!code || _id == null) return acc
            acc[code] = [...(acc[code] ?? []), { _id, formTitle }]
            return acc
        }, {})

        const batches = Object.entries(groups)
        if (!batches.length) return

        setSending(true)
        const results = await Promise.all(
            batches.map(([templateCode, payload]) => dispatch(sendNotification({ templateCode, payload }))),
        )
        setSending(false)

        const outcome = results.reduce(
            (acc, res) => {
                Object.entries(res.payload?.channels ?? {}).forEach(([channel, group]) => {
                    const failed = group.notifications ?? []
                    const skipped = group.skipped ?? []
                    const tally = (acc.byChannel[channel] ??= { sent: 0, failed: 0, skipped: 0 })

                    tally.sent += group.sentCount ?? 0
                    tally.failed += failed.length
                    tally.skipped += skipped.length

                    acc.sent += group.sentCount ?? 0
                    acc.failed.push(...failed.map(item => ({ ...item, channel })))
                    acc.skipped.push(...skipped.map(item => ({ ...item, channel })))
                })
                return acc
            },
            { sent: 0, byChannel: {}, failed: [], skipped: [] },
        )

        if (outcome.sent) {
            const parts = Object.entries(outcome.byChannel)
                .filter(([, tally]) => tally.sent > 0)
                .map(([channel, tally]) => {
                    const names = CHANNEL_NAMES[channel] ?? CHANNEL_NAMES.default

                    return `${tally.sent} ${t(tally.sent === 1 ? names.one : names.many)}`
                })

            snackbar.success(parts.length ? `${parts.join(' · ')} ${t('sent.')}` : t('Notification sent.'))
        }

        if (outcome.failed.length || outcome.skipped.length) {
            setDialog({ mode: 'result', result: outcome })
        } else {
            setDialog(null)
            setNotifyAll(false)
        }
    }

    const handleReset = row => setDialog({ mode: 'reset', row })

    const confirmReset = async () => {
        const responseId = resetCode(target)
        if (responseId == null) return

        setResetting(true)
        const res = await dispatch(resetForm({ id: responseId }))
        setResetting(false)

        if (res.payload?.id) {
            onRowReset?.(res.payload.id)
            snackbar.success(t('Form reset.'))
            setDialog(null)
        } else {
            snackbar.error(t('Could not reset the form.'))
        }
    }

    const columns = [
        ...visibleCols(cols).map(col => ({
            field: col,
            headerName: col.replaceAll('_', ' ').toUpperCase(),
            flex: 1,
            minWidth: 150,
        })),
        ...(hasNotifyCol(cols)
            ? [
                {
                    field: '__notifyAction__',
                    headerName: '',
                    sortable: false,
                    filterable: false,
                    disableExport: true,
                    width: 130,
                    renderCell: ({ row }) =>
                        needsNotify(row) ? (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={event => {
                                    event.stopPropagation()
                                    handleClick(row)
                                }}
                            >
                                {t('Notify')}
                            </Button>
                        ) : null,
                },
            ]
            : []),

        ...(hasResetCol(cols) && (tableData ?? []).some(row => needsReset(row))
            ? [
                {
                    field: '__resetAction__',
                    headerName: '',
                    sortable: false,
                    filterable: false,
                    disableExport: true,
                    width: 130,
                    renderCell: ({ row }) =>
                        needsReset(row) ? (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={event => {
                                    event.stopPropagation()
                                    handleReset(row)
                                }}
                            >
                                {t('Reset')}
                            </Button>
                        ) : null,
                },
            ]
            : []),
    ]

    const rows = tableData.map((row, index) => ({
        id: index,
        ...row,
    }))

    return (
        <Paper sx={{ p: 3, mt: 1 }}>
            <Stack direction="row" spacing={1} mb={2} justifyContent="flex-end">
                <ExportButton
                    columns={columns}
                    rows={rows}
                    fileName="report"
                    sheetName="Report"
                    variant="contained"
                />
            </Stack>

            <Box width="100%" sx={{ overflowX: 'auto' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={100}
                    rowsPerPageOptions={[100]}
                    pagination
                    disableRowSelectionOnClick
                    density="compact"
                    onRowClick={(params) => {
                        if (params.row._path) {
                            window.open(params.row._path, '_blank', 'noopener');
                        }
                    }}
                    getRowClassName={(params) =>
                        params.row._path ? 'clickable-row' : ''
                    }
                    sx={{
                        height: 'calc(100vh - 395px)',
                        overflow: 'auto',
                        '& .clickable-row': {
                            cursor: 'pointer',
                        },
                    }}
                />
            </Box>

            <Dialog open={Boolean(dialog)} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>
                    {isResult ? t('Notification Results') : isReset ? t('Reset Form') : t('Send Notification')}
                </DialogTitle>
                <DialogContent dividers>
                    {isResult ? (
                        <Stack spacing={2}>
                            <Stack spacing={0.75}>
                                {Object.entries(dialog.result.byChannel ?? {}).map(([channel, tally]) => (
                                    <Stack key={channel} direction="row" alignItems="center" spacing={1}>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            color={tally.failed ? 'error' : 'success'}
                                            label={t(channelLabel(channel))}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            {`${tally.sent} ${t('sent')} · ${tally.failed} ${t('failed')} · ${tally.skipped} ${t('skipped')}`}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>

                            {dialog.result.skipped.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" color="warning.main">
                                        {`${t('Skipped')} (${dialog.result.skipped.length})`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('No reachable contact on file for these students.')}
                                    </Typography>
                                    <Stack sx={{ mt: 0.5 }}>
                                        {dialog.result.skipped.map((item, index) => (
                                            <Typography key={`${item.id}-${index}`} variant="body2">
                                                {item.channel && (
                                                    <Typography component="span" variant="caption" color="text.secondary">
                                                        {`${t(channelLabel(item.channel))} · `}
                                                    </Typography>
                                                )}
                                                {nameFor(item.id)}
                                                {item.reason ? (
                                                    <Typography component="span" variant="caption" color="text.secondary">
                                                        {` — ${item.reason}`}
                                                    </Typography>
                                                ) : null}
                                            </Typography>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {dialog.result.failed.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" color="error.main">
                                        {`${t('Failed')} (${dialog.result.failed.length})`}
                                    </Typography>
                                    <Stack sx={{ mt: 0.5 }}>
                                        {dialog.result.failed.map((item, index) => (
                                            <Typography key={`${item.id ?? 'failed'}-${index}`} variant="body2">
                                                {item.channel && (
                                                    <Typography component="span" variant="caption" color="text.secondary">
                                                        {`${t(channelLabel(item.channel))} · `}
                                                    </Typography>
                                                )}
                                                {item.subjectName || t('Unknown recipient')}
                                                {item.reason || item.status ? (
                                                    <Typography component="span" variant="caption" color="text.secondary">
                                                        {` — ${item.reason || item.status}`}
                                                    </Typography>
                                                ) : null}
                                            </Typography>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Stack>
                    ) : isReset ? (
                        <Stack spacing={1}>
                            <Stack direction="row" spacing={1}>
                                <Typography variant="body1">{`${t('Reset the form response for')}`}</Typography>
                                <Typography variant="body1" fontWeight={900}>{targetName}</Typography>
                            </Stack>
                        </Stack>
                    ) : notifyAll ? (
                        <Typography variant="body2" fontWeight={900}>
                            {`${t('All parents/guardians who have not signed will be notified')} (${pendingRows.length}).`}
                        </Typography>
                    ) : (
                        <Stack direction={'row'} spacing={1}>
                            <Typography variant="body1">{`${t("Send a reminder to the parent or guadian of")}`}</Typography>
                            <Typography variant='body1' fontWeight={900}>{targetName}</Typography>
                        </Stack>
                    )}

                    {isNotify && (
                        <>
                            <Divider sx={{ my: 2 }} />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={notifyAll}
                                        disabled={sending}
                                        onChange={event => setNotifyAll(event.target.checked)}
                                    />
                                }
                                label={`${t('Notify all parents/guardians pending signature')}`}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {isResult ? (
                        <Button variant="contained" onClick={closeDialog}>
                            {t('CLOSE')}
                        </Button>
                    ) : (
                        <>
                            <Button onClick={closeDialog} disabled={sending || resetting}>
                                {t('CANCEL')}
                            </Button>
                            {isReset ? (
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={confirmReset}
                                    disabled={resetting || resetCode(target) == null}
                                    startIcon={resetting ? <CircularProgress size={20} /> : null}
                                >
                                    {resetting ? t('RESETTING...') : t('RESET')}
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={handleSend}
                                    disabled={sending || !recipients.length}
                                    startIcon={sending ? <CircularProgress size={20} /> : null}
                                >
                                    {sending
                                        ? t('SENDING...')
                                        : `${t('SEND')}${recipients.length > 1 ? ` (${recipients.length})` : ''}`}
                                </Button>
                            )}
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Paper>
    )
}
